import InputAdornment, {
  inputAdornmentClasses,
} from '@mui/material/InputAdornment';
import OutlinedInput, {
  outlinedInputClasses,
} from '@mui/material/OutlinedInput';
import { styled } from '@mui/material/styles';

import {
  FIELD_ADORNMENT_GAP_PX,
  FIELD_ICON_PX,
  FIELD_PADDING_PX,
} from 'theme/components/field-geometry';
import { INPUT_DIRTY_DOT_PX } from 'ui/input/input.constants';
import type { InputSize, InputTextAlign } from 'ui/input/input.types';

interface FieldSizeOwnerState {
  fieldSize: InputSize;
}

interface InputFieldOwnerState extends FieldSizeOwnerState {
  textAlign: InputTextAlign;
}

const OWNER_STATE_PROPS: PropertyKey[] = ['fieldSize', 'textAlign'];

const shouldForwardProp = (prop: PropertyKey) =>
  !OWNER_STATE_PROPS.includes(prop);

export const InputField = styled(OutlinedInput, {
  shouldForwardProp,
})<InputFieldOwnerState>(({ fieldSize, textAlign }) => ({
  [`&&.${outlinedInputClasses.adornedStart}`]: {
    paddingLeft: FIELD_PADDING_PX[fieldSize].x,
  },
  [`&&.${outlinedInputClasses.adornedEnd}`]: {
    paddingRight: FIELD_PADDING_PX[fieldSize].x,
  },
  [`&&.${outlinedInputClasses.adornedStart} .${outlinedInputClasses.input}`]: {
    paddingLeft: 0,
  },
  [`&&.${outlinedInputClasses.adornedEnd} .${outlinedInputClasses.input}`]: {
    paddingRight: 0,
  },
  [`& .${outlinedInputClasses.input}`]: { textAlign },
}));

export const InputAdornmentSlot = styled(InputAdornment, {
  shouldForwardProp,
})<FieldSizeOwnerState>(({ theme, fieldSize }) => ({
  height: 'auto',
  maxHeight: 'none',
  margin: 0,
  gap: FIELD_ADORNMENT_GAP_PX[fieldSize],
  color: theme.palette.text.secondary,
  [`&.${inputAdornmentClasses.positionStart}`]: {
    marginRight: FIELD_ADORNMENT_GAP_PX[fieldSize],
  },
  [`&.${inputAdornmentClasses.positionEnd}`]: {
    marginLeft: FIELD_ADORNMENT_GAP_PX[fieldSize],
  },
  '& > svg': {
    width: FIELD_ICON_PX[fieldSize],
    height: FIELD_ICON_PX[fieldSize],
  },
}));

export const InputDirtyDot = styled('span')(({ theme }) => ({
  flexShrink: 0,
  width: INPUT_DIRTY_DOT_PX,
  height: INPUT_DIRTY_DOT_PX,
  borderRadius: '50%',
  backgroundColor: theme.palette.secondary.main,
}));
